import { getUuid } from '@sibe/utilities/common';
import {
  HsColor,
  HsGraphicObject,
  HsIndexedGeometry,
  HsIndexedGeometryData,
  HsMatrix4,
  HsTextureMaterial,
  HsTileVisibilityPass,
  hsMakeTexturedIndexedGeometry,
  type HsBox,
  type HsDisposable,
  type HsGraphicQueue,
  type HsGraphics,
  type HsHlodTilesetLink,
  type HsPerspectiveCamera,
  type HsShader,
  type HsVisibilityDraw,
  type HsVisibilityResult,
} from '@sibe/hyperscene';
import {
  hsSelectHlodTextureTargets,
  type HsHlodTextureTargets,
  type HsHlodTranscodedTexture,
} from '../../../shared/hlod/basis-transcode.function';
import type { HsHlodPayloadCache, HsHlodPayloadCacheStats } from './hlod-payload-cache.class';
import { HsHlodTextureTranscoder } from './hlod-texture-transcoder.class';
import {
  HS_DEFAULT_HLOD_TILESET_OPTIONS,
  HsHlodTileset,
  hsIsHlodManifest,
  type HsHlodTilesetLoadStatus,
  type HsHlodTilesetStats,
  type HsHlodUpdateResult,
  type HsHlodView,
} from './hlod-tileset.class';
import {
  HsHlodTilesetLookup,
  type HsHlodLookupSource,
  type HsHlodTilesetLookupOptions,
} from './hlod-tileset-lookup.class';

/** Frames between visibility passes while the camera moves. */
const VISIBILITY_INTERVAL_FRAMES: number = 6;

/** One decoded HLOD node, as `formats/hlod`'s `hsDecodeHlodPayload` returns it. */
export interface HsPhotogrammetryNodeGeometry {
  positions: Float32Array;
  normals: Float32Array;
  uvs: Float32Array;
  indices: Uint32Array;
  texture: Uint8Array;
  textureCodec: 'jpeg' | 'ktx2';
}

/** The endpoints the source reads; `HsViewerApi` provides them. */
export interface HsPhotogrammetryApi extends HsHlodLookupSource {
  getDocuments(documentIds: readonly string[]): Promise<Map<string, Blob>>;
}

/** Where nodes are drawn: a viewer's context, graphics and queue. */
export interface HsPhotogrammetryTarget {
  gl: WebGL2RenderingContext;
  graphics: HsGraphics;
  queue: Pick<HsGraphicQueue, 'addObject' | 'removeObject'>;
}

export interface HsPhotogrammetrySourceOptions {
  api: HsPhotogrammetryApi;
  /** Decodes a node payload with positions relative to `origin`; pass `formats/hlod`'s `hsDecodeHlodPayload`. */
  decodeNode(payload: ArrayBuffer, origin: readonly [number, number, number]): Promise<HsPhotogrammetryNodeGeometry>;
  /** Creates a worker running `workers/hlod-transcode`'s handler, for KTX2 atlases. */
  createTranscodeWorker(): Worker;
  /** Folder with the Basis Universal transcoder (`assets/decoders/basis/`). */
  basisDecoderUrl: string;
  /** Downloads tileset documents (manifests, nodes); default `api.getDocuments` read as buffers. */
  downloadDocuments?(documentIds: string[]): Promise<Map<string, ArrayBuffer>>;
  /**
   * Keeps manifests, subtrees and node payloads across sessions and serves them before `downloadDocuments` is
   * asked; none by default. The host owns the cache (its budget, `enabled` flag and `stats`).
   */
  payloadCache?: HsHlodPayloadCache | null;
  /** Transcode workers (default 1: one keeps up with downloads and leaves the main thread alone). */
  transcodeWorkers?: number;
  /** The viewer to draw into, or null before it exists. */
  target(): HsPhotogrammetryTarget | null;
  /**
   * The scene objects drawing `meshId`: the coarse root mesh a tileset refines. It is hidden once the
   * tileset's own root node is drawn and supplies the material and placement of the nodes.
   */
  rootObjects(meshId: string): HsGraphicObject[];
  /** Increases when the scene changes; work for an older epoch is dropped. */
  currentEpoch(): number;
  requestRender(): void;
  /** Called when nodes were added or removed; the viewer re-sorts and re-indexes its queue. */
  onObjectsChanged?(): void;
  /** Diagnostic events (`hlod.visibility`). */
  onEvent?(name: string, detail: string): void;
  onWarning?(message: string, error: unknown): void;
  lookup?: HsHlodTilesetLookupOptions;
}

/** What a frame's `update` changed. */
export interface HsPhotogrammetryUpdate {
  /** Nodes or root meshes changed visibility or placement; the viewer must re-cull. */
  changed: boolean;
  /** Nodes are still loading or refining; draw another frame. */
  needsFrame: boolean;
  /** Photogrammetry is still streaming for this view; `status().busy` as of this update. */
  busy: boolean;
}

/**
 * The streaming work left for the current view, summed over the tilesets whose root mesh the scene draws.
 * `busy` is false once the view is stable: every manifest and subtree is loaded, no node is wanted, in flight
 * or waiting for its upload or retry, and every tileset in view draws its own root. Hosts treat that as "settled".
 */
export interface HsPhotogrammetryLoadStatus {
  busy: boolean;
  /** Tilesets being refined: known, with their root mesh in the scene. */
  tilesets: number;
  /** Tileset manifests still downloading after a lookup. */
  manifestsLoading: number;
  /** Subtree manifests downloading. */
  subtreesLoading: number;
  /** Nodes requested and not decoded yet. */
  nodesLoading: number;
  /** Nodes the view wants but that are not requested yet. */
  nodesQueued: number;
  /** Nodes decoded and waiting for their GPU upload. */
  nodesDecoded: number;
  /** Nodes whose download or upload failed, including those waiting for a retry. */
  nodesFailed: number;
  /** Nodes or subtrees the view wants whose retry time has not come; they are requested again by a later update. */
  nodesRetrying: number;
  /** Node batches and subtree manifests in flight. */
  requestsInFlight: number;
  /**
   * Bytes of node payloads and subtree manifests brought in since the tilesets were created, from the network or
   * the payload cache; `HsPhotogrammetrySource.cacheStats()` tells them apart.
   */
  downloadedBytes: number;
  /** Every refined tileset whose root is in view draws its own root node; true without tilesets. */
  rootReady: boolean;
}

/** Events of a photogrammetry source. */
export interface HsPhotogrammetrySourceEvents {
  /** The load status changed: after an update, a manifest download or a reset. Not emitted for equal values. */
  status: HsPhotogrammetryLoadStatus;
}

const HS_IDLE_PHOTOGRAMMETRY_STATUS: HsPhotogrammetryLoadStatus = {
  busy: false,
  tilesets: 0,
  manifestsLoading: 0,
  subtreesLoading: 0,
  nodesLoading: 0,
  nodesQueued: 0,
  nodesDecoded: 0,
  nodesFailed: 0,
  nodesRetrying: 0,
  requestsInFlight: 0,
  downloadedBytes: 0,
  rootReady: true,
};

/** CPU cost of the occlusion visibility passes; the latest 512 samples are kept for percentiles. */
export interface HsPhotogrammetryVisibilityStats {
  passes: number;
  totalMs: number;
  averageMs: number;
  samples: number[];
  submitSamples: number[];
}

interface TilesetEntry {
  meshId: string;
  tileset: HsHlodTileset<HsGraphicObject>;
  /** The root mesh was in the scene at the last update, so the tileset is refined and counts as busy. */
  active: boolean;
  /** Payload-frame position of the root mesh's local origin, known once that mesh is drawn. */
  origin: [number, number, number] | null;
  sourceMaterial: HsTextureMaterial<HsShader> | null;
  textures: Map<HsGraphicObject, { texture: WebGLTexture; bytes: number }>;
}

interface DecodedNode {
  geometry: HsPhotogrammetryNodeGeometry;
  /** A decoded JPEG atlas, or a KTX2 atlas transcoded to the device's GPU format. */
  texture: { bitmap: ImageBitmap; transcoded?: never } | { transcoded: HsHlodTranscodedTexture; bitmap?: never };
}

interface TranscoderEntry {
  transcoder: HsHlodTextureTranscoder;
  targets: HsHlodTextureTargets;
  /** Compressed texture extensions are enabled per context, so a new context needs new targets. */
  gl: WebGL2RenderingContext;
}

/**
 * Streams photogrammetry as HLOD tilesets: finds the tilesets of drawn root meshes, refines their nodes by
 * projected error every frame within a GPU budget, optionally hides occluded nodes with a tile-ID visibility
 * pass, and replaces the root mesh once the tileset's own root is drawn.
 */
export class HsPhotogrammetrySource {
  /** Projected error (px) below which a node is not refined. */
  public maxScreenErrorPixels: number = HS_DEFAULT_HLOD_TILESET_OPTIONS.maxScreenErrorPixels;
  /** GPU memory for the nodes of all tilesets together. */
  public gpuBudgetBytes: number = HS_DEFAULT_HLOD_TILESET_OPTIONS.gpuBudgetBytes;
  /** Skip refining nodes the visibility pass found hidden. */
  public occlusionRefinement: boolean = HS_DEFAULT_HLOD_TILESET_OPTIONS.occlusionRefinement;
  public readonly visibility: HsPhotogrammetryVisibilityStats = {
    passes: 0,
    totalMs: 0,
    averageMs: 0,
    samples: [],
    submitSamples: [],
  };
  /** GPU texture formats of the uploaded nodes, for diagnostics. */
  public readonly textureFormats: Set<string> = new Set();
  private readonly entries: Map<string, TilesetEntry> = new Map();
  private readonly loading: Set<string> = new Set();
  private readonly listeners: {
    [K in keyof HsPhotogrammetrySourceEvents]: Set<(value: HsPhotogrammetrySourceEvents[K]) => void>;
  } = { status: new Set() };
  private lastStatus: HsPhotogrammetryLoadStatus = HS_IDLE_PHOTOGRAMMETRY_STATUS;
  private readonly lookupClient: HsHlodTilesetLookup;
  private transcoderEntry: TranscoderEntry | null = null;
  private visibilityPass: HsTileVisibilityPass<HsGraphicObject> | null = null;
  private visibilityFramesSincePass: number = 0;
  private visibilityPollTimer: ReturnType<typeof setTimeout> | null = null;
  private visibilityRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly visibilityLastCamera: HsMatrix4 = HsMatrix4.makeIdentityMatrix();
  private readonly visibilityPreviousFrameCamera: HsMatrix4 = HsMatrix4.makeIdentityMatrix();
  private visibilityStale: boolean = true;

  public constructor(private readonly options: HsPhotogrammetrySourceOptions) {
    this.lookupClient = new HsHlodTilesetLookup(options.api, options.lookup);
  }

  /** True while lookups wait out the backoff after a failure. */
  public get coolingDown(): boolean {
    return this.lookupClient.coolingDown;
  }

  /** Tileset manifests still downloading. */
  public get loadingTilesets(): number {
    return this.loading.size;
  }

  /** True when no tileset is known yet. */
  public get empty(): boolean {
    return this.entries.size === 0;
  }

  /** The persistent payload cache the source reads through, or null without one. */
  public get payloadCache(): HsHlodPayloadCache | null {
    return this.options.payloadCache ?? null;
  }

  /** Hit, miss and byte counters of the payload cache, or null without one. */
  public cacheStats(): HsHlodPayloadCacheStats | null {
    return this.options.payloadCache?.stats ?? null;
  }

  /** Calls `listener` on every `event` until the returned handle is disposed. */
  public on<K extends keyof HsPhotogrammetrySourceEvents>(
    event: K,
    listener: (value: HsPhotogrammetrySourceEvents[K]) => void
  ): HsDisposable {
    const listeners: Set<(value: HsPhotogrammetrySourceEvents[K]) => void> = this.listeners[event];
    listeners.add(listener);
    return { dispose: (): void => void listeners.delete(listener) };
  }

  /**
   * The streaming work left for the current view, as of the last update. `update` and manifest downloads
   * refresh it; subscribe to `status` instead of polling.
   */
  public status(): HsPhotogrammetryLoadStatus {
    return this.lastStatus;
  }

  /** Asks which of the drawn `meshIds` have tilesets and sets those up; stops when the scene changes. */
  public async lookup(sceneId: string | null, meshIds: readonly string[]): Promise<void> {
    const epoch: number = this.options.currentEpoch();
    await this.lookupClient.lookup(
      sceneId,
      [...meshIds],
      () => epoch === this.options.currentEpoch(),
      (link: HsHlodTilesetLink) => void this.createTileset(link, epoch)
    );
  }

  /**
   * Selects the nodes to draw for `camera` (call it every frame before culling): places each tileset on its
   * root mesh, refines within the budget, shows or hides nodes and roots, and runs the visibility pass.
   */
  public update(camera: HsPerspectiveCamera): HsPhotogrammetryUpdate {
    const target: HsPhotogrammetryTarget | null = this.options.target();
    if (this.entries.size === 0 || target === null) {
      return { changed: false, needsFrame: false, busy: this.refreshStatus().busy };
    }
    const gl: WebGL2RenderingContext = target.gl;
    camera.updateFrustum();
    const matrix: ArrayLike<number> = camera.worldMatrix.values;
    const view: HsHlodView = {
      cameraPosition: [matrix[12], matrix[13], matrix[14]],
      planes: camera.frustum.planes,
      focalPixels: gl.drawingBufferHeight / (2 * Math.tan(camera.fov / 2)),
      near: camera.near,
    };
    let changed: boolean = false;
    let needsFrame: boolean = false;
    this.applyVisibility(this.visibilityPass?.poll() ?? null);
    this.entries.forEach((entry: TilesetEntry) => {
      const roots: HsGraphicObject[] = this.options.rootObjects(entry.meshId);
      const root: HsGraphicObject | undefined = roots[0];
      if (root === undefined || !(root.material instanceof HsTextureMaterial)) {
        // The model is not part of the scene right now. Nodes decoded for it would never be uploaded, so their
        // CPU copies are dropped; they are downloaded again if the model comes back and the view wants them.
        entry.active = false;
        entry.tileset.discardDecoded();
        entry.textures.forEach((_: unknown, object: HsGraphicObject) => {
          changed = changed || object.visible;
          object.visible = false;
        });
        return;
      }
      entry.active = true;
      if (entry.origin === null) {
        const bounds: number[] = entry.tileset.rootRecord.bounds;
        const box: HsBox = root.geometry.boundingBox;
        entry.origin = [bounds[0] - box.min.x, bounds[1] - box.min.y, bounds[2] - box.min.z];
      }
      if (entry.sourceMaterial !== root.material) {
        // Visual settings rebuilt the scene materials and deleted their shader programs.
        entry.sourceMaterial = root.material;
        entry.textures.forEach((value: { texture: WebGLTexture }, object: HsGraphicObject) => {
          object.material = this.makeMaterial(gl, root.material as HsTextureMaterial<HsShader>, value.texture);
        });
      }
      const world: number[] = Array.from(root.worldMatrix.values);
      const [ox, oy, oz]: [number, number, number] = entry.origin;
      // Payload position p is drawn at world · (p - origin).
      const transform: number[] = [...world];
      for (let row: number = 0; row < 3; row++) {
        transform[12 + row] = world[12 + row] - world[row] * ox - world[4 + row] * oy - world[8 + row] * oz;
      }
      entry.tileset.setTransform(transform);
      entry.tileset.options = {
        ...entry.tileset.options,
        maxScreenErrorPixels: this.maxScreenErrorPixels,
        // One budget for all photogrammetry models in the scene.
        gpuBudgetBytes: this.gpuBudgetBytes / this.entries.size,
        occlusionRefinement: this.occlusionRefinement,
      };
      const result: HsHlodUpdateResult<HsGraphicObject> = entry.tileset.update(view);
      const drawn: Set<HsGraphicObject> = new Set<HsGraphicObject>(result.render);
      entry.textures.forEach((_: unknown, object: HsGraphicObject) => {
        const visible: boolean = drawn.has(object);
        if (object.visible !== visible) {
          object.visible = visible;
          changed = true;
        }
        if (!object.worldMatrix.equals(root.worldMatrix, 0)) {
          object.worldMatrix.copy(root.worldMatrix);
          object.updateBoundingSphere();
          changed = true;
        }
      });
      const showRoot: boolean = !entry.tileset.rootReady;
      roots.forEach((object: HsGraphicObject) => {
        if (object.visible !== showRoot) {
          object.visible = showRoot;
          changed = true;
        }
      });
      needsFrame = needsFrame || result.needsFrame;
    });
    this.submitVisibilityPass(camera, gl, changed);
    return { changed, needsFrame, busy: this.refreshStatus().busy };
  }

  /** Re-runs the visibility pass on the next frame, for example after the occlusion setting changed. */
  public invalidateVisibility(): void {
    this.visibilityStale = true;
  }

  /** The tilesets, for diagnostics and benchmarks. */
  public tilesets(): HsHlodTileset<HsGraphicObject>[] {
    return [...this.entries.values()].map((entry: TilesetEntry) => entry.tileset);
  }

  /** Per-tileset counters. */
  public stats(): HsHlodTilesetStats[] {
    return this.tilesets().map((tileset: HsHlodTileset<HsGraphicObject>) => tileset.stats());
  }

  /** The node objects drawn now. */
  public drawnNodes(): HsGraphicObject[] {
    const nodes: HsGraphicObject[] = [];
    this.entries.forEach((entry: TilesetEntry) =>
      entry.textures.forEach((_: unknown, object: HsGraphicObject) => {
        if (object.visible && !object.isDeleted) {
          nodes.push(object);
        }
      })
    );
    return nodes;
  }

  /** Bytes and count of the uploaded node textures. */
  public textureTotals(): { bytes: number; count: number } {
    let bytes: number = 0;
    let count: number = 0;
    this.entries.forEach((entry: TilesetEntry) =>
      entry.textures.forEach((value: { bytes: number }) => {
        bytes += value.bytes;
        count++;
      })
    );
    return { bytes, count };
  }

  /** Forgets every tileset and its nodes, for example when the scene changes. */
  public reset(): void {
    this.lookupClient.reset();
    this.entries.forEach((entry: TilesetEntry) => entry.tileset.dispose());
    this.entries.clear();
    this.textureFormats.clear();
    this.disposeVisibilityPass();
    this.loading.clear();
    this.refreshStatus();
  }

  public dispose(): void {
    this.reset();
    this.transcoderEntry?.transcoder.dispose();
    this.transcoderEntry = null;
  }

  /** Sums the tileset statuses, keeps the result and tells listeners when it differs from the last one. */
  private refreshStatus(): HsPhotogrammetryLoadStatus {
    const status: HsPhotogrammetryLoadStatus = {
      ...HS_IDLE_PHOTOGRAMMETRY_STATUS,
      manifestsLoading: this.loading.size,
    };
    this.entries.forEach((entry: TilesetEntry) => {
      if (!entry.active) {
        return;
      }
      const tileset: HsHlodTilesetLoadStatus = entry.tileset.loadStatus();
      status.tilesets++;
      status.subtreesLoading += tileset.subtreesLoading;
      status.nodesLoading += tileset.nodesLoading;
      status.nodesQueued += tileset.nodesQueued;
      status.nodesDecoded += tileset.nodesDecoded;
      status.nodesFailed += tileset.nodesFailed;
      status.nodesRetrying += tileset.nodesRetrying;
      status.requestsInFlight += tileset.requestsInFlight;
      status.downloadedBytes += tileset.downloadedBytes;
      status.rootReady = status.rootReady && (tileset.rootReady || !tileset.rootInView);
    });
    status.busy =
      status.manifestsLoading > 0 ||
      status.subtreesLoading > 0 ||
      status.nodesLoading > 0 ||
      status.nodesQueued > 0 ||
      status.nodesDecoded > 0 ||
      status.nodesRetrying > 0 ||
      status.requestsInFlight > 0 ||
      !status.rootReady;
    const last: HsPhotogrammetryLoadStatus = this.lastStatus;
    this.lastStatus = status;
    if (
      (Object.keys(status) as (keyof HsPhotogrammetryLoadStatus)[]).some(
        (key: keyof HsPhotogrammetryLoadStatus) => status[key] !== last[key]
      )
    ) {
      this.listeners.status.forEach((listener: (value: HsPhotogrammetryLoadStatus) => void) => listener(status));
    }
    return status;
  }

  private async createTileset(link: HsHlodTilesetLink, epoch: number): Promise<void> {
    // Scene geometry ids are compared trimmed and lower-cased, like the scene source's.
    const meshId: string = link.meshId.trim().toLowerCase();
    if (this.entries.has(meshId) || this.loading.has(meshId)) {
      return;
    }
    this.loading.add(meshId);
    this.refreshStatus();
    try {
      const data: ArrayBuffer | undefined = (await this.downloadBuffers([link.manifestDocumentId])).get(
        link.manifestDocumentId
      );
      if (epoch !== this.options.currentEpoch()) {
        return;
      }
      const manifest: unknown = data === undefined ? null : JSON.parse(new TextDecoder().decode(data));
      if (!hsIsHlodManifest(manifest)) {
        throw new Error(`Document ${link.manifestDocumentId} is not an HLOD tileset manifest.`);
      }
      const entry: TilesetEntry = {
        meshId,
        active: true,
        origin: null,
        sourceMaterial: null,
        textures: new Map(),
        tileset: null as unknown as HsHlodTileset<HsGraphicObject>,
      };
      entry.tileset = new HsHlodTileset<HsGraphicObject>(
        manifest,
        {
          download: (documentIds: string[]) => this.downloadBuffers(documentIds),
          decode: (_: unknown, payload: ArrayBuffer) => this.decodeNode(entry, payload),
          upload: (_: unknown, decoded: unknown) => this.uploadNode(entry, decoded as DecodedNode),
          discard: (decoded: unknown) => (decoded as DecodedNode).texture.bitmap?.close(),
          decodedBytes: (decoded: unknown) => decodedBytes(decoded as DecodedNode),
          dispose: (object: HsGraphicObject) => this.disposeNode(entry, object),
        },
        () => this.options.requestRender(),
        { ...HS_DEFAULT_HLOD_TILESET_OPTIONS }
      );
      this.entries.set(meshId, entry);
      this.options.requestRender();
    } catch (error: unknown) {
      this.options.onWarning?.('HLOD tileset setup failed.', error);
    } finally {
      this.loading.delete(meshId);
      this.refreshStatus();
    }
  }

  private downloadBuffers(documentIds: string[]): Promise<Map<string, ArrayBuffer>> {
    const cache: HsHlodPayloadCache | null = this.options.payloadCache ?? null;
    return cache === null
      ? this.downloadFromApi(documentIds)
      : cache.fetch(documentIds, (missing: string[]) => this.downloadFromApi(missing));
  }

  private async downloadFromApi(documentIds: string[]): Promise<Map<string, ArrayBuffer>> {
    if (this.options.downloadDocuments !== undefined) {
      return this.options.downloadDocuments(documentIds);
    }
    const blobs: Map<string, Blob> = await this.options.api.getDocuments(documentIds);
    const buffers: Map<string, ArrayBuffer> = new Map();
    for (const [id, blob] of blobs) {
      buffers.set(id, await blob.arrayBuffer());
    }
    return buffers;
  }

  private async decodeNode(entry: TilesetEntry, payload: ArrayBuffer): Promise<DecodedNode> {
    if (entry.origin === null) {
      throw new Error('HLOD tileset origin is not known yet.');
    }
    const geometry: HsPhotogrammetryNodeGeometry = await this.options.decodeNode(payload, entry.origin);
    if (geometry.textureCodec === 'ktx2') {
      return { geometry, texture: { transcoded: await this.transcoder().transcoder.transcode(geometry.texture) } };
    }
    // Same decoder options as scene textures, so node colors match the imported root mesh.
    const bitmap: ImageBitmap = await createImageBitmap(new Blob([geometry.texture], { type: 'image/jpeg' }));
    return { geometry, texture: { bitmap } };
  }

  private transcoder(): TranscoderEntry {
    const gl: WebGL2RenderingContext | undefined = this.options.target()?.gl;
    if (this.transcoderEntry === null || this.transcoderEntry.gl !== gl) {
      if (gl === undefined) {
        throw new Error('HLOD textures need a WebGL context to pick a GPU format.');
      }
      this.transcoderEntry?.transcoder.dispose();
      const targets: HsHlodTextureTargets = hsSelectHlodTextureTargets(gl);
      this.transcoderEntry = {
        gl,
        targets,
        transcoder: new HsHlodTextureTranscoder(
          () => this.options.createTranscodeWorker(),
          targets,
          this.options.basisDecoderUrl,
          this.options.transcodeWorkers ?? 1
        ),
      };
    }
    return this.transcoderEntry;
  }

  private uploadNode(entry: TilesetEntry, decoded: DecodedNode): { handle: HsGraphicObject; bytes: number } | null {
    const target: HsPhotogrammetryTarget | null = this.options.target();
    const material: HsTextureMaterial<HsShader> | null = entry.sourceMaterial;
    try {
      if (target === null || material === null) {
        return null;
      }
      const { gl, graphics, queue } = target;
      const { positions, normals, indices, uvs }: HsPhotogrammetryNodeGeometry = decoded.geometry;
      const geometry: HsIndexedGeometry | null = hsMakeTexturedIndexedGeometry(
        graphics,
        new HsIndexedGeometryData(positions, normals, indices, uvs)
      );
      const texture: WebGLTexture | null = gl.createTexture();
      if (geometry === null || texture === null) {
        return null;
      }
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      const { bitmap, transcoded } = decoded.texture;
      let textureBytes: number;
      if (transcoded !== undefined) {
        this.uploadTranscodedTexture(gl, transcoded);
        textureBytes = transcoded.bytes;
        this.textureFormats.add(`KTX2 as ${transcoded.target.toUpperCase()}`);
      } else {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, bitmap);
        gl.generateMipmap(gl.TEXTURE_2D);
        textureBytes = Math.ceil((bitmap.width * bitmap.height * 16) / 3);
        this.textureFormats.add('JPEG as RGBA8');
      }
      gl.bindTexture(gl.TEXTURE_2D, null);
      const object: HsGraphicObject = new HsGraphicObject(
        getUuid(),
        'hlod-node',
        geometry,
        this.makeMaterial(gl, material, texture)
      );
      object.visible = false;
      queue.addObject(object);
      entry.textures.set(object, { texture, bytes: textureBytes });
      this.options.onObjectsChanged?.();
      return {
        handle: object,
        bytes: textureBytes + (positions.length + normals.length + uvs.length + indices.length) * 4,
      };
    } finally {
      decoded.texture.bitmap?.close();
    }
  }

  /** Uploads every transcoded mip level; block-compressed textures cannot generate their own mips. */
  private uploadTranscodedTexture(gl: WebGL2RenderingContext, texture: HsHlodTranscodedTexture): void {
    const internalFormat: number | undefined = this.transcoderEntry?.targets.internalFormats[texture.target];
    texture.levels.forEach((level: HsHlodTranscodedTexture['levels'][number], index: number) => {
      if (texture.target === 'rgba8' || internalFormat === undefined) {
        gl.texImage2D(
          gl.TEXTURE_2D,
          index,
          gl.RGBA,
          level.width,
          level.height,
          0,
          gl.RGBA,
          gl.UNSIGNED_BYTE,
          level.data
        );
      } else {
        gl.compressedTexImage2D(gl.TEXTURE_2D, index, internalFormat, level.width, level.height, 0, level.data);
      }
    });
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAX_LEVEL, texture.levels.length - 1);
  }

  private makeMaterial(
    gl: WebGL2RenderingContext,
    source: HsTextureMaterial<HsShader>,
    texture: WebGLTexture
  ): HsTextureMaterial<HsShader> {
    // Shares the scene material's compiled shader; makeCopy would recompile it and reset its uniforms,
    // which turns every mesh drawn with that shader black.
    const material: HsTextureMaterial<HsShader> = new HsTextureMaterial(gl, source.getShader(), false);
    material.color = HsColor.makeCopy(source.color);
    material.textureRect = [...source.textureRect];
    material.texture = texture;
    return material;
  }

  private disposeNode(entry: TilesetEntry, object: HsGraphicObject): void {
    const target: HsPhotogrammetryTarget | null = this.options.target();
    target?.queue.removeObject(object);
    this.options.onObjectsChanged?.();
    const texture: { texture: WebGLTexture } | undefined = entry.textures.get(object);
    entry.textures.delete(object);
    if (target !== null && !object.isDeleted) {
      if (texture !== undefined) {
        target.gl.deleteTexture(texture.texture);
      }
      if (!object.geometry.isDeleted) {
        target.graphics.deleteBuffer(object.geometry.buffer);
        if (object.geometry instanceof HsIndexedGeometry) {
          target.graphics.deleteBuffer(object.geometry.indexBuffer);
        }
        object.geometry.isDeleted = true;
      }
    }
    object.isDeleted = true;
  }

  /**
   * Starts a visibility pass of the drawn nodes every few frames while the camera moves, and once more
   * when it stops or the drawn set changes, until the occlusion state has settled.
   */
  private submitVisibilityPass(
    camera: HsPerspectiveCamera,
    gl: WebGL2RenderingContext,
    drawnSetChanged: boolean
  ): void {
    if (!this.occlusionRefinement) {
      return;
    }
    const moving: boolean = !camera.worldMatrix.equals(this.visibilityPreviousFrameCamera, 0.001);
    this.visibilityPreviousFrameCamera.copy(camera.worldMatrix);
    this.visibilityStale =
      this.visibilityStale || drawnSetChanged || !camera.worldMatrix.equals(this.visibilityLastCamera, 0.001);
    this.visibilityFramesSincePass++;
    const unsettled: boolean = [...this.entries.values()].some(
      (entry: TilesetEntry) => entry.tileset.occlusionUnsettled
    );
    if (!this.visibilityStale && !unsettled) {
      return;
    }
    if ((moving && this.visibilityFramesSincePass < VISIBILITY_INTERVAL_FRAMES) || this.visibilityPass?.busy === true) {
      // The viewer stops drawing once the camera rests, so ask for one more frame to check the final view.
      this.visibilityRetryTimer ??= setTimeout(() => {
        this.visibilityRetryTimer = null;
        this.options.requestRender();
      }, 100);
      return;
    }
    const draws: HsVisibilityDraw<HsGraphicObject>[] = [];
    this.entries.forEach((entry: TilesetEntry) =>
      entry.textures.forEach((_: unknown, object: HsGraphicObject) => {
        if (object.visible && !object.isDeleted && object.geometry instanceof HsIndexedGeometry) {
          draws.push({
            key: object,
            buffer: object.geometry.buffer,
            indexBuffer: object.geometry.indexBuffer,
            indexCount: object.geometry.indexArrayLength,
            modelMatrix: object.worldMatrix.values,
          });
        }
      })
    );
    try {
      this.visibilityPass ??= new HsTileVisibilityPass<HsGraphicObject>(gl);
      if (
        !this.visibilityPass.submit(
          draws,
          camera.worldMatrixInverse.values,
          camera.projectionMatrix.values,
          gl.drawingBufferWidth / Math.max(1, gl.drawingBufferHeight)
        )
      ) {
        return;
      }
    } catch (error: unknown) {
      // Without the pass every node simply counts as visible, as before.
      this.options.onWarning?.('HLOD visibility pass failed; refining without occlusion.', error);
      this.occlusionRefinement = false;
      return;
    }
    this.visibilityFramesSincePass = 0;
    this.visibilityStale = false;
    this.visibilityLastCamera.copy(camera.worldMatrix);
    this.scheduleVisibilityPoll();
  }

  /** Polls the fence off the render loop, so an idle viewer still receives the result. */
  private scheduleVisibilityPoll(): void {
    if (this.visibilityPollTimer !== null) {
      return;
    }
    this.visibilityPollTimer = setTimeout(() => {
      this.visibilityPollTimer = null;
      const pass: HsTileVisibilityPass<HsGraphicObject> | null = this.visibilityPass;
      if (pass === null || !pass.busy) {
        return;
      }
      const result: HsVisibilityResult<HsGraphicObject> | null = pass.poll();
      if (result === null) {
        this.scheduleVisibilityPoll();
        return;
      }
      if (this.applyVisibility(result)) {
        this.options.requestRender();
      }
    }, 16);
  }

  /** Returns whether the result changed a node between occluded and visible, or another check is due. */
  private applyVisibility(result: HsVisibilityResult<HsGraphicObject> | null): boolean {
    if (result === null) {
      return false;
    }
    const stats: HsPhotogrammetryVisibilityStats = this.visibility;
    stats.passes++;
    stats.totalMs += result.cpuMs;
    stats.averageMs = stats.totalMs / stats.passes;
    stats.samples.push(result.cpuMs);
    stats.submitSamples.push(result.submitMs);
    if (stats.samples.length > 512) {
      stats.samples.shift();
      stats.submitSamples.shift();
    }
    this.options.onEvent?.(
      'hlod.visibility',
      `nodes=${result.checked.length};ms=${result.cpuMs.toFixed(2)};submitMs=${result.submitMs.toFixed(2)}`
    );
    const visible: Set<HsGraphicObject> = new Set<HsGraphicObject>(result.pixels.keys());
    let changed: boolean = false;
    this.entries.forEach((entry: TilesetEntry) => {
      const checked: HsGraphicObject[] = result.checked.filter((object: HsGraphicObject) => entry.textures.has(object));
      changed = entry.tileset.applyVisibility(checked, visible) || entry.tileset.occlusionUnsettled || changed;
    });
    return changed;
  }

  private disposeVisibilityPass(): void {
    if (this.visibilityPollTimer !== null) {
      clearTimeout(this.visibilityPollTimer);
      this.visibilityPollTimer = null;
    }
    if (this.visibilityRetryTimer !== null) {
      clearTimeout(this.visibilityRetryTimer);
      this.visibilityRetryTimer = null;
    }
    this.visibilityPass?.dispose();
    this.visibilityPass = null;
    this.visibilityStale = true;
  }
}

function decodedBytes(decoded: DecodedNode): number {
  const { positions, normals, uvs, indices, texture }: HsPhotogrammetryNodeGeometry = decoded.geometry;
  const { bitmap, transcoded } = decoded.texture;
  return (
    positions.byteLength +
    normals.byteLength +
    uvs.byteLength +
    indices.byteLength +
    texture.byteLength +
    (transcoded !== undefined ? transcoded.bytes : bitmap.width * bitmap.height * 4)
  );
}
